import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';

import InputNumber from 'modules/_shared/components/InputNumber';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

import { IncentiveOfferSellerParticipationLimits } from 'modules/incentivesMarket/types';

import BuyOfferSellerLimitsPanel from './BuyOfferSellerLimitsPanel';
import BuyOfferSellerLimitsPreview from './BuyOfferSellerLimitsPreview';

type BuyOfferUnitsSectionProps = {
  sharesToTransfer: number;
  sharesToTransferLabel: string;
  sharesToTransferInfo?: string;
  sellerParticipationLimits: IncentiveOfferSellerParticipationLimits;
  unitPrice: number;
  /* eslint-disable-next-line no-unused-vars */
  onChangeSharesToTransfer: (value: number) => void;
  /* eslint-disable-next-line no-unused-vars */
  onChangeAllowSellAll: (allowSellAll: boolean) => void;
  /* eslint-disable-next-line no-unused-vars */
  onChangeSellerLimit: (
    field: keyof IncentiveOfferSellerParticipationLimits,
    value: number
  ) => void;
};

const BuyOfferUnitsSection: FC<BuyOfferUnitsSectionProps> = ({
  sharesToTransfer,
  sharesToTransferLabel,
  sharesToTransferInfo,
  sellerParticipationLimits,
  unitPrice,
  onChangeSharesToTransfer,
  onChangeAllowSellAll,
  onChangeSellerLimit,
}) => {
  const { t } = useTranslate();
  const isCustomLimits = sellerParticipationLimits.allowSellAll === false;

  return (
    <>
      <div className="mb-4">
        <h6 className="overline-title text-soft mb-2">
          {t('IncentivesMarketBuyOfferTotalUnits')}
        </h6>
        <p className="text-soft fs-13px mb-3">
          {sharesToTransferInfo || t('IncentivesMarketBuyOfferTotalUnitsDescription')}
        </p>
        <div className="row">
          <div className="col-md-6">
            <InputNumber
              id="shares-to-transfer-input"
              label={sharesToTransferLabel}
              value={sharesToTransfer}
              decimalScale={2}
              onChange={onChangeSharesToTransfer}
              required
            />
          </div>
        </div>
      </div>

      <hr />

      <div className="mb-3">
        <h6 className="overline-title text-soft mb-2">
          {t('IncentivesMarketSellerLimitsTitle')}
        </h6>
        <p className="text-soft fs-13px mb-3">
          {t('IncentivesMarketSellerLimitsDescription')}
        </p>

        <div className="d-flex w-100 mb-3" style={{ gap: '8px' }}>
          <Button
            className="flex-fill"
            onClick={() => onChangeAllowSellAll(true)}
            variant={
              !isCustomLimits ? ButtonVariants.PRIMARY : ButtonVariants.LIGHT
            }
            data-testid="seller-limits-no-cap"
          >
            <span className="text-center">{t('IncentivesMarketSellerLimitsNoCap')}</span>
          </Button>
          <Button
            className="flex-fill"
            onClick={() => onChangeAllowSellAll(false)}
            variant={
              isCustomLimits ? ButtonVariants.PRIMARY : ButtonVariants.LIGHT
            }
            data-testid="seller-limits-custom"
          >
            <span className="text-center">
              {t('IncentivesMarketSellerLimitsCustom')}
            </span>
          </Button>
        </div>

        {isCustomLimits && (
          <BuyOfferSellerLimitsPanel
            sellerParticipationLimits={sellerParticipationLimits}
            onChangeLimit={onChangeSellerLimit}
          />
        )}
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <BuyOfferSellerLimitsPreview
            sellerParticipationLimits={sellerParticipationLimits}
            sharesToTransfer={sharesToTransfer}
            unitPrice={unitPrice}
          />
        </div>
      </div>
    </>
  );
};

export default BuyOfferUnitsSection;
